import { useState, type DragEvent, type ReactNode, type Ref } from 'react';
import fileUploadImg from '@/assets/images/file-upload.webp';
import { Spinner, ExportCurve } from '@/components/icons';
import { cn } from '@/lib/utils';

type FileDropzoneVariant = 'card' | 'row';

type FileDropzoneMark = 'illustration' | 'icon';

type FileDropzoneProps = {
    title: ReactNode;
    description?: ReactNode;
    selectLabel?: ReactNode;
    accept?: string;
    multiple?: boolean;
    disabled?: boolean;
    loading?: boolean;
    variant?: FileDropzoneVariant;
    /**
     * The illustration is the default — this component's whole job is the
     * action the art depicts, and the same drawing already stands for "enviar
     * arquivo" on the anamnesis start screen.
     *
     * `icon` is the fallback for a host too cramped to seat the art; no
     * dropzone needs it today.
     */
    mark?: FileDropzoneMark;
    showSelectButton?: boolean;
    inputRef?: Ref<HTMLInputElement>;
    className?: string;
    onFilesSelected: (files: File[]) => void;
};

export function FileDropzone({
    title,
    description,
    selectLabel = 'Selecionar arquivo',
    accept,
    multiple = false,
    disabled = false,
    loading = false,
    variant = 'card',
    mark = 'illustration',
    showSelectButton = true,
    inputRef,
    className,
    onFilesSelected,
}: FileDropzoneProps) {
    const [isDragging, setIsDragging] = useState(false);
    const isDisabled = disabled || loading;

    const handleFiles = (selectedFiles: File[]) => {
        if (isDisabled || selectedFiles.length === 0) return;
        onFilesSelected(multiple ? selectedFiles : selectedFiles.slice(0, 1));
    };

    const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (isDisabled) return;
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
        if (
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
        ) {
            return;
        }

        setIsDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        setIsDragging(false);
        handleFiles(Array.from(event.dataTransfer.files));
    };

    return (
        <label
            aria-disabled={isDisabled}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
                'group block rounded-3xl border border-dashed border-border-strong bg-surface transition-colors',
                isDragging && 'border-blue-300 bg-blue-50/70',
                isDisabled
                    ? 'cursor-not-allowed opacity-70'
                    : 'cursor-pointer hover:border-border-strong hover:bg-muted/70',
                variant === 'card' && 'p-6 text-center',
                variant === 'row' && 'p-5',
                className,
            )}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                disabled={isDisabled}
                className="hidden"
                onChange={(event) => {
                    handleFiles(Array.from(event.currentTarget.files ?? []));
                    event.currentTarget.value = '';
                }}
            />

            <div
                className={cn(
                    variant === 'card' && 'flex flex-col items-center',
                    variant === 'row' &&
                        'flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between',
                )}
            >
                <div
                    className={cn(
                        variant === 'card' && 'flex flex-col items-center',
                        variant === 'row' && 'flex items-center gap-3',
                    )}
                >
                    {mark === 'illustration' ? (
                        // The art dims and holds its place while the upload
                        // runs, with the spinner over it. Swapping it for a
                        // small badge would jump the box shorter at exactly the
                        // moment the user is watching it — and the mark is the
                        // only non-textual progress signal in the two anamnesis
                        // dropzones, which render no progress bar.
                        <span
                            className={cn(
                                'relative flex justify-center',
                                variant === 'card' ? 'mb-3' : 'shrink-0',
                            )}
                        >
                            <img
                                src={fileUploadImg}
                                alt=""
                                // Intrinsic size, so the box is reserved rather
                                // than reflowing when the art lands.
                                width={476}
                                height={387}
                                decoding="async"
                                className={cn(
                                    'h-auto max-w-full transition-opacity',
                                    variant === 'card' ? 'w-[140px]' : 'w-[88px]',
                                    loading && 'opacity-40',
                                )}
                            />
                            {loading && (
                                // `aria-hidden`: the title already reads
                                // "Enviando...", and saying it twice is noise.
                                <span
                                    aria-hidden
                                    className="absolute inset-0 flex items-center justify-center"
                                >
                                    <Spinner className="size-6 animate-spin text-blue-600" />
                                </span>
                            )}
                        </span>
                    ) : (
                        <span
                            className={cn(
                                'flex shrink-0 items-center justify-center rounded-2xl bg-card text-blue-600 shadow-sm',
                                variant === 'card' ? 'mb-3 size-14' : 'size-11',
                            )}
                        >
                            {loading ? (
                                <Spinner className="size-6 animate-spin" />
                            ) : (
                                <ExportCurve className="size-6" />
                            )}
                        </span>
                    )}

                    <span className={cn(variant === 'card' && 'block')}>
                        <span className="block text-sm font-semibold text-foreground">
                            {loading ? 'Enviando...' : title}
                        </span>
                        {description && (
                            <span className="mt-1 block text-xs text-muted-foreground">
                                {description}
                            </span>
                        )}
                    </span>
                </div>

                {showSelectButton && (
                    <span
                        className={cn(
                            'inline-flex h-10 items-center justify-center rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-none transition-colors group-hover:bg-surface-muted',
                            variant === 'row' && 'w-full lg:w-auto',
                        )}
                    >
                        {selectLabel}
                    </span>
                )}
            </div>
        </label>
    );
}
